import React, { useMemo } from "react";

const STEPS = [
  { id: "account",   label: "Create Entra ID account",               section: "Entra ID" },
  { id: "manager",   label: "Set manager",                            section: "Entra ID" },
  { id: "liccheck",  label: "Check license availability",             section: "Licensing" },
  { id: "licassign", label: "Assign license group",                   section: "Licensing" },
  { id: "mailbox",   label: "Wait for mailbox provisioning (2 min)",  section: "Licensing" },
  { id: "groups",    label: "Assign distribution & security groups",  section: "Groups" },
  { id: "email",     label: "Send confirmation email",                section: "Notification" },
];

const PATTERNS = {
  account:   { success: /Entra ID account (created|already existed)/,  error: /ERROR.*Create Entra|ERROR.*Retrieve existing/ },
  manager:   { success: /Manager (set|No manager specified)/,           error: /ERROR.*Set manager/ },
  liccheck:  { success: /License check:/,                               error: null },
  licassign: { success: /Added to SG-IC-|Already a member of SG-IC-/,  error: /Insufficient seats/ },
  mailbox:   { success: /Mailbox wait complete/,                        error: null },
  groups:    { success: /Group assignments complete/,                   error: null },
  email:     { success: /Completion email sent/,                        error: /ERROR.*Send completion/ },
};

const SECTIONS = [...new Set(STEPS.map((s) => s.section))];

function parseStates(transactionLog, isRunning, isPendingLicense) {
  const lines = (transactionLog || "").split("\n");
  const states = {};

  for (const step of STEPS) {
    const { success, error } = PATTERNS[step.id];
    if (error && lines.some((l) => error.test(l))) {
      states[step.id] = isPendingLicense && step.id === "licassign" ? "pending-license" : "failed";
    } else if (success && lines.some((l) => success.test(l))) {
      states[step.id] = "completed";
    } else {
      states[step.id] = "pending";
    }
  }

  if (isRunning && !isPendingLicense) {
    const first = STEPS.find((s) => states[s.id] === "pending");
    if (first) states[first.id] = "in-progress";
  }

  return states;
}

function StepIcon({ state }) {
  if (state === "completed") {
    return (
      <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="#16a34a" />
        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (state === "failed") {
    return (
      <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="#dc2626" />
        <path d="M7 7L13 13M13 7L7 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === "pending-license") {
    return (
      <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="#f59e0b" />
        <path d="M10 6v5l3 2" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === "in-progress") {
    return (
      <span className="step-icon-spin-wrapper" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" stroke="#e5e7eb" strokeWidth="2" fill="none" />
          <circle cx="10" cy="10" r="9" stroke="#f59e0b" strokeWidth="2" fill="none"
            strokeDasharray="20 38" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return (
    <svg className="step-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="9" stroke="#d1d5db" strokeWidth="2" fill="none" />
    </svg>
  );
}

function HeaderIcon({ isRunning, isPendingLicense, hasFailures }) {
  if (isPendingLicense) {
    return (
      <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.15)" />
        <path d="M22 14v9l5 3" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (isRunning) {
    return (
      <span className="exec-header-spinner" aria-hidden="true">
        <svg width="44" height="44" viewBox="0 0 44 44">
          <circle cx="22" cy="22" r="19" stroke="rgba(255,255,255,0.2)" strokeWidth="3" fill="none" />
          <circle cx="22" cy="22" r="19" stroke="white" strokeWidth="3" fill="none"
            strokeDasharray="80 40" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (hasFailures) {
    return (
      <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.15)" />
        <path d="M15 15L29 29M29 15L15 29" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.15)" />
      <path d="M13 22L19 28L31 16" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function OnboardingExecutionProgress({ transactionNumber, employeeName, transactionLog, isRunning, isPendingLicense }) {
  const stepStates    = useMemo(() => parseStates(transactionLog, isRunning, isPendingLicense), [transactionLog, isRunning, isPendingLicense]);
  const completedCount = STEPS.filter((s) => stepStates[s.id] === "completed").length;
  const failedCount    = STEPS.filter((s) => stepStates[s.id] === "failed").length;
  const activeStep     = STEPS.find((s) => stepStates[s.id] === "in-progress");
  const progressPct    = Math.round(((completedCount + (isPendingLicense ? 2 : failedCount)) / STEPS.length) * 100);

  const headerTitle = isRunning
    ? "Processing Onboarding"
    : isPendingLicense
    ? "Awaiting License Approval"
    : failedCount > 0
    ? "Completed with Errors"
    : "Onboarding Complete";

  return (
    <div className="exec-overlay">
      <div className="exec-modal">

        <div className={`exec-header${isPendingLicense ? " exec-header-warning" : ""}`}>
          <HeaderIcon isRunning={isRunning} isPendingLicense={isPendingLicense} hasFailures={failedCount > 0} />
          <div className="exec-header-text">
            <h2>{headerTitle}</h2>
            <p className="exec-txn">{transactionNumber}</p>
            {employeeName && <p className="exec-employee">{employeeName}</p>}
          </div>
        </div>

        <div className="exec-body">
          {SECTIONS.map((section) => (
            <div key={section} className="exec-section">
              <div className="exec-section-title">{section}</div>
              {STEPS.filter((s) => s.section === section).map((step) => {
                const state = stepStates[step.id];
                return (
                  <div key={step.id} className={`exec-step exec-step-${state}`}>
                    <StepIcon state={state} />
                    <span className="exec-step-label">{step.label}</span>
                  </div>
                );
              })}
            </div>
          ))}

          {isPendingLicense && (
            <div className="exec-pending-license-note">
              <p>
                <strong>License approval required.</strong> An email has been sent to
                the IT administrator. Automation will resume after licenses are purchased
                and the approval link in the email is clicked.
              </p>
              <p>You can close this window — the request is tracked in history.</p>
              <p>
                Check the live status in SharePoint:{" "}
                <a href="https://serveubrands.sharepoint.com/sites/Automation/Lists/OnboardingApp" target="_blank" rel="noopener noreferrer">
                  Automation site &gt; OnboardingApp list
                </a>
              </p>
            </div>
          )}
        </div>

        <div className="exec-footer">
          <p className="exec-status-text">
            {isRunning
              ? activeStep
                ? `Running: ${activeStep.label}…`
                : "Initializing…"
              : isPendingLicense
              ? "Paused — awaiting license approval from IT"
              : `${completedCount} of ${STEPS.length} steps completed${failedCount > 0 ? ` · ${failedCount} failed` : ""} — Redirecting…`}
          </p>
          <div className="exec-progress-bar">
            <div className="exec-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

      </div>
    </div>
  );
}
